import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Card, PageHeader } from '../components/ui';
import { outreachAPI } from '../lib/api';

interface Question {
  id: string;
  type: 'short_text' | 'long_text' | 'single_select' | 'multi_select' | 'nps' | 'numeric' | 'date' | 'email';
  question: string;
  required: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

interface OutreachDetail {
  id: string;
  name: string;
  description?: string | null;
  type?: string | null;
  status?: string | null;
  definition_json?: { questions?: Question[] } | null;
  collectors?: Array<{ slug: string; is_active: boolean }>;
}

type AnswerValue = string | string[];

export default function RequestDetailScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useMemo(() => new Date().toISOString(), []);

  const { data: item, isLoading } = useQuery<OutreachDetail>(['citizen-request', id], async () => {
    const { data } = await outreachAPI.getItem(id);
    return data.data;
  });

  const questions = item?.definition_json?.questions || [];
  const collectorSlug = item?.collectors?.find((c) => c.is_active)?.slug;

  const allRequiredAnswered = questions
    .filter((q) => q.required)
    .every((q) => {
      const a = answers[q.id];
      return Array.isArray(a) ? a.length > 0 : !!a;
    });

  function setA(qid: string, val: AnswerValue) {
    setAnswers((a) => ({ ...a, [qid]: val }));
  }
  function toggleA(qid: string, val: string) {
    const cur = Array.isArray(answers[qid]) ? (answers[qid] as string[]) : [];
    setA(qid, cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]);
  }

  async function submit() {
    if (!collectorSlug) {
      toast.error('This request is not accepting responses right now.');
      return;
    }
    setSubmitting(true);
    try {
      await outreachAPI.respond(collectorSlug, {
        answers: { questions: answers },
        started_at: startedAt,
      });
      setDone(true);
      setTimeout(() => navigate('/citizen/home'), 2000);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Could not submit your response. Please try again.';
      toast.error(msg === 'profanity_detected' ? 'Please review your wording and resubmit.' : msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading || !item) {
    return (
      <div className="flex flex-col screen-enter">
        <PageHeader title="Request" onBack={() => navigate('/citizen/requests')} />
        <p className="text-sm text-[color:var(--muted-foreground)] px-6 py-10">Loading…</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex flex-col min-h-screen screen-enter">
        <PageHeader title="" onBack={() => navigate('/citizen/requests')} />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-5">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full" style={{ background: '#D9EBD9' }} />
            <div className="absolute inset-2 rounded-full" style={{ background: '#9ECC9E' }} />
            <div className="relative w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#2D5A2D' }}>
              <Check className="w-7 h-7 text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-extrabold tracking-tight">Thank you!</h2>
            <p className="text-sm text-[color:var(--muted-foreground)] max-w-[280px]">
              Your response was recorded. Returning home…
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isSurvey = (item.type || '').toLowerCase() !== 'rsvp';

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title={isSurvey ? 'Survey' : 'RSVP'} onBack={() => navigate('/citizen/requests')} />

      <div className="flex-1 px-6 pb-24">
        <Card className="p-4 mb-5 border-0">
          <h1 className="text-lg font-extrabold leading-tight">{item.name}</h1>
          {item.description && <p className="text-xs mt-2 text-[color:var(--muted-foreground)]">{item.description}</p>}
        </Card>

        {questions.length === 0 ? (
          <p className="text-sm text-[color:var(--muted-foreground)]">
            This request has no questions to answer right now.
          </p>
        ) : (
          <>
            <p className="text-sm text-[color:var(--muted-foreground)] mb-5">
              {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </p>

            <div className="space-y-6">
              {questions.map((q, i) => (
                <div key={q.id} className="space-y-3">
                  <label className="text-[15px] font-semibold leading-snug block">
                    {i + 1}. {q.question}
                    {q.required && <span className="ml-1" style={{ color: '#C24A4A' }}>*</span>}
                  </label>

                  {q.type === 'single_select' &&
                    (q.options || []).map((opt) => {
                      const on = answers[q.id] === opt.value;
                      return (
                        <SelectButton key={opt.value} on={on} radio onClick={() => setA(q.id, opt.value)}>
                          {opt.label}
                        </SelectButton>
                      );
                    })}

                  {q.type === 'multi_select' &&
                    (q.options || []).map((opt) => {
                      const on = (answers[q.id] as string[] | undefined)?.includes(opt.value) || false;
                      return (
                        <SelectButton key={opt.value} on={on} onClick={() => toggleA(q.id, opt.value)}>
                          {opt.label}
                        </SelectButton>
                      );
                    })}

                  {(q.type === 'long_text' || q.type === 'short_text') &&
                    (q.type === 'long_text' ? (
                      <textarea
                        value={(answers[q.id] as string) || ''}
                        onChange={(e) => setA(q.id, e.target.value)}
                        placeholder={q.placeholder}
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl text-sm resize-none focus:outline-none"
                        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                      />
                    ) : (
                      <input
                        value={(answers[q.id] as string) || ''}
                        onChange={(e) => setA(q.id, e.target.value)}
                        placeholder={q.placeholder}
                        className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                      />
                    ))}

                  {(q.type === 'numeric' || q.type === 'nps') && (
                    <input
                      type="number"
                      value={(answers[q.id] as string) || ''}
                      onChange={(e) => setA(q.id, e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                    />
                  )}

                  {q.type === 'date' && (
                    <input
                      type="date"
                      value={(answers[q.id] as string) || ''}
                      onChange={(e) => setA(q.id, e.target.value)}
                      className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                    />
                  )}

                  {q.type === 'email' && (
                    <input
                      type="email"
                      value={(answers[q.id] as string) || ''}
                      onChange={(e) => setA(q.id, e.target.value)}
                      placeholder={q.placeholder}
                      className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                      style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                    />
                  )}
                </div>
              ))}
            </div>

            <Button
              size="lg"
              className="w-full rounded-xl mt-7"
              disabled={!allRequiredAnswered || submitting || !collectorSlug}
              onClick={submit}
            >
              {submitting ? 'Submitting…' : isSurvey ? 'Submit feedback' : 'Register'}
            </Button>

            <p className="text-[11px] text-center mt-3 text-[color:var(--muted-foreground)]">
              Your response will be shared with the requesting organization. <span className="underline">Citizen Standard</span>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function SelectButton({
  on,
  radio,
  onClick,
  children,
}: {
  on: boolean;
  radio?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left hover-elevate active-elevate-2"
      style={{ background: on ? 'var(--accent)' : 'var(--card)', borderColor: on ? 'var(--primary)' : 'var(--border)' }}
    >
      <span
        className={`w-4 h-4 ${radio ? 'rounded-full' : 'rounded'} border-2 flex items-center justify-center shrink-0`}
        style={{ borderColor: on ? 'var(--primary)' : 'var(--border)', background: on ? 'var(--primary)' : 'transparent' }}
      >
        {on && (radio ? <span className="w-1.5 h-1.5 rounded-full bg-white" /> : <Check className="w-3 h-3 text-white" strokeWidth={3} />)}
      </span>
      <span className="text-sm font-medium">{children}</span>
    </button>
  );
}
